<template>
  <main class="grow">
    <section class="overflow-hidden bg-slate-950 text-white">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="grid gap-10 pt-32 pb-14 md:pt-40 md:pb-20 lg:grid-cols-[1fr_0.95fr] lg:items-center lg:gap-14 lg:pt-44">
          <div class="min-w-0">
            <h1 class="max-w-3xl text-[2.05rem] font-extrabold leading-[1.08] tracking-normal text-white sm:text-5xl lg:text-[3.6rem]">
              Win the work, do the work, get paid.
            </h1>
            <p class="mt-6 max-w-2xl text-base leading-7 tracking-normal text-slate-300 sm:text-lg">
              {{ hubCardCount }} features in one workspace for freelancers, consultants and small agencies,
              sorted by the job they do, with the plan each one starts on.
            </p>
            <div class="fh-actions mt-8 flex flex-wrap gap-3">
              <a
                :href="addReferralToUrl('https://app.workspace369.com/signup')"
                class="hub-primary"
                data-conversion-event="start_free_click"
                data-conversion-label="Features hub primary CTA"
                data-conversion-location="/features"
              >
                Start free trial
              </a>
              <NuxtLink
                :to="localePath('/pricing')"
                class="hub-secondary"
                data-conversion-event="pricing_click"
                data-conversion-label="Features hub hero pricing"
                data-conversion-location="/features"
              >
                Compare plans
              </NuxtLink>
            </div>
            <TrialDisclosure on-dark />
          </div>

          <nav class="job-index" aria-labelledby="job-index-title">
            <p id="job-index-title" class="job-index-title">Jump to a job</p>
            <ol>
              <li v-for="(group, index) in hubGroups" :key="group.id">
                <a :href="`#${group.id}`">
                  <span class="job-num" translate="no">{{ String(index + 1).padStart(2, "0") }}</span>
                  <span class="job-copy">
                    <strong>{{ group.eyebrow }}</strong>
                    <small>{{ group.cards.slice(0, 3).map((card) => card.name).join(", ") }}</small>
                  </span>
                  <span class="job-count">{{ group.cards.length }}<span class="sr-only"> features</span></span>
                </a>
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </section>

    <FeaturesList />

    <FeatureTutorial v-if="featureTutorial" :tutorial="featureTutorial" />

    <!-- Closing: one line for this page instead of the sitewide stats band. -->
    <section class="bg-slate-950 text-white" data-sticky-cta-hide>
      <div class="hub-final mx-auto max-w-6xl px-4 sm:px-6">
        <h2>Start with the job that takes most of your week.</h2>
        <p class="hub-final-lede">Try it on your own clients and invoices, then add the rest as you need it.</p>
        <div class="mt-8 flex flex-wrap justify-center gap-3">
          <a
            :href="addReferralToUrl('https://app.workspace369.com/signup')"
            class="hub-primary"
            data-conversion-event="start_free_click"
            data-conversion-label="Features hub final CTA"
            data-conversion-location="/features"
          >
            Start free trial
          </a>
          <NuxtLink
            :to="localePath('/pricing')"
            class="hub-secondary"
            data-conversion-event="pricing_click"
            data-conversion-label="Features hub final pricing"
            data-conversion-location="/features"
          >
            Compare plans
          </NuxtLink>
        </div>
        <TrialDisclosure on-dark centered />
      </div>
    </section>
    <PhoneStickyCta label="Features hub" :location="$route.path" watch=".fh-actions" />
  </main>
</template>

<script setup>
import featureVideos from "#build/page-data/feature-videos.mjs";
import { localizedUrl } from "~/data/locales";
import { hubCardCount, hubGroups } from "~/data/features-hub";

const featureTutorial = featureVideos["/features"];

const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const { addReferralToUrl } = useReferral();
const siteUrl = "https://workspace369.com";
const canonicalUrl = computed(() => localizedUrl(siteUrl, "/features", canonicalLocaleCode.value));

const title = "Workspace369 Features | CRM, Proposals, Invoicing, AI & More";
const description =
  "Every Workspace369 feature, sorted by job: win work, do the work, get paid, talk to clients, run on AI and run the business, with the plan each one starts on.";

useHead(() => ({
  title,
  meta: [
    { name: "description", content: description },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
  ],
  link: [{ key: "canonical", rel: "canonical", href: canonicalUrl.value }],
  script: [
    {
      key: "workspace369-features-schema",
      type: "application/ld+json",
      // The same cards, in the same order, as the page shows.
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "@id": `${canonicalUrl.value}#feature-list`,
        name: "Workspace369 features",
        url: canonicalUrl.value,
        numberOfItems: hubCardCount,
        itemListElement: hubGroups
          .flatMap((group) => group.cards)
          .map((card, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: card.name,
            url: localizedUrl(siteUrl, card.link, canonicalLocaleCode.value),
          })),
      }),
    },
  ],
}));
</script>

<style scoped>
.hub-final {
  padding-top: 5.5rem;
  padding-bottom: 5.5rem;
  text-align: center;
}
.hub-final h2 {
  margin: 0 auto;
  max-width: 40rem;
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 800;
  line-height: 1.12;
}
.hub-final-lede {
  margin: 1rem auto 0;
  max-width: 34rem;
  color: #cbd5e1;
  font-size: 1.05rem;
  line-height: 1.6;
}

.hub-primary,
.hub-secondary {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 0.75rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 900;
  line-height: 1;
  transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease;
}

.hub-primary {
  border: 1px solid #0e7490;
  background: #0e7490;
  color: #fff;
}

.hub-secondary {
  border: 1px solid rgb(148 163 184 / 0.45);
  color: #fff;
}

.hub-primary:hover,
.hub-primary:focus-visible {
  border-color: #155e75;
  background: #155e75;
  transform: translateY(-1px);
}

.hub-secondary:hover,
.hub-secondary:focus-visible {
  border-color: #cbd5e1;
  background: rgb(255 255 255 / 0.06);
  transform: translateY(-1px);
}

.job-index {
  min-width: 0;
  border: 1px solid rgb(148 163 184 / 0.18);
  border-radius: 10px;
  background: #020617;
  box-shadow: 0 22px 66px rgb(0 0 0 / 0.28);
  padding: 0.5rem;
}

.job-index-title {
  padding: 0.75rem 0.85rem 0.5rem;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
}

.job-index a {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  border-radius: 8px;
  padding: 0.75rem 0.85rem;
  transition: background-color 150ms ease;
}

.job-index li + li a {
  border-top: 1px solid rgb(148 163 184 / 0.12);
}

.job-index a:hover,
.job-index a:focus-visible {
  background: rgb(103 232 249 / 0.08);
}

.job-index a:focus-visible {
  outline: 2px solid #67e8f9;
  outline-offset: -2px;
}

.job-num {
  flex: none;
  border: 1px solid rgb(103 232 249 / 0.35);
  border-radius: 4px;
  padding: 0.1rem 0.35rem;
  color: #67e8f9;
  font-size: 0.75rem;
  font-weight: 850;
  font-variant-numeric: tabular-nums;
}

.job-copy {
  display: grid;
  min-width: 0;
  flex: 1;
  gap: 0.15rem;
}

.job-copy strong {
  color: #fff;
  font-size: 1rem;
  font-weight: 850;
}

.job-copy small {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Phones: the index becomes one row of jobs that scrolls sideways (native overflow, no scroll
   capture), so the hero ends on the actions instead of six stacked rows. */
@media (max-width: 639px) {
  .job-index { border: 0; background: none; box-shadow: none; padding: 0; }
  .job-index-title { padding: 0 0 0.5rem; }
  .job-index ol { display: flex; gap: 0.5rem; overflow-x: auto; padding-bottom: 0.25rem; scroll-snap-type: x proximity; }
  .job-index li { flex: none; scroll-snap-align: start; }
  .job-index a { border: 1px solid rgb(148 163 184 / 0.25); gap: 0.5rem; padding: 0.55rem 0.7rem; }
  .job-index li + li a { border-top: 1px solid rgb(148 163 184 / 0.25); }
  .job-copy small, .job-count { display: none; }
  .job-copy strong { font-size: 0.9rem; white-space: nowrap; }
}

.job-count {
  flex: none;
  color: #cbd5e1;
  font-size: 0.85rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
</style>
